import type { MewsAllocation, MewsCategory } from '@/services/platform/data/booking/mews/types';

/**
 * Opening allocation for a category the customer has just picked: as few units as the party fits
 * into, with the guests spread evenly across them.
 *
 * One copy for all three Mews flows. It was three before — exported from `flows/sunbed/index.tsx`,
 * imported from there by the experience flow, and re-declared verbatim inside `flows/stay/index.tsx`
 * — which is the sort of drift that follows from a flow owning shared code.
 */
export function buildInitialAllocations(
  category: MewsCategory,
  adults: number,
  children: number
): MewsAllocation[] {
  const maxCap = Math.max(category.maxCapacity, 1);
  const qty = Math.ceil((adults + children) / maxCap);

  let remAdults = adults;
  let remChildren = children;

  const guestDistribution = Array.from({ length: qty }, (_, i) => {
    const slotsLeft = qty - i;
    const totalHere = Math.min(maxCap, Math.ceil((remAdults + remChildren) / slotsLeft));
    const adultsHere = Math.min(remAdults, totalHere);
    const childrenHere = Math.min(remChildren, totalHere - adultsHere);
    remAdults -= adultsHere;
    remChildren -= childrenHere;
    return { unitIndex: i, adults: adultsHere, children: childrenHere };
  });

  return [
    {
      categoryId: category.id,
      categoryName: category.name,
      pricePerDay: category.pricePerDay,
      quantity: qty,
      guestDistribution,
    },
  ];
}
